import { useEffect, useMemo, useRef, useState } from "react";
import { kundenLaden } from "@/components/KundenSuche";
import { Search, Loader2, Check, X } from "lucide-react";

/**
 * Kunden aus dem Bestand auswählen – für Dialoge, in denen ein Kunde
 * getroffen werden muss, statt seinen Namen abzutippen.
 *
 * Der Unterschied zur Kopfleisten-Suche: Dort führt ein Treffer in die
 * Kundenakte, hier wird er zurückgegeben. Die Kundenliste teilen sich beide,
 * sie wird je Sitzung nur einmal geladen.
 *
 * Gesucht wird über Name, Kundennummer und Ort. Der Ort ist bei den Laboren
 * oft das Merkmal, an dem sich ähnlich klingende Namen unterscheiden lassen.
 */
/**
 * `freitext`: erlaubt, einen Namen einzutragen, der noch nicht im Kundenstamm
 * steht (neuer Kunde, Interessent). Die Wahl kommt dann als
 * { name, customer_id: null, manuell: true } zurück.
 */
export default function KundenAuswahl({ wert, onWahl, autoFocus = false, testId = "kundenauswahl", freitext = false }) {
  const [begriff, setBegriff] = useState("");
  const [kunden, setKunden] = useState(null);
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const kasten = useRef(null);
  const feld = useRef(null);

  useEffect(() => { kundenLaden().then(setKunden); }, []);
  useEffect(() => { setAktiv(0); }, [begriff]);

  useEffect(() => {
    const zu = (e) => { if (kasten.current && !kasten.current.contains(e.target)) setOffen(false); };
    document.addEventListener("mousedown", zu);
    return () => document.removeEventListener("mousedown", zu);
  }, []);

  const treffer = useMemo(() => {
    const q = begriff.trim().toLowerCase();
    if (!q || !kunden) return [];
    return kunden
      .filter((k) => (k.name || "").toLowerCase().includes(q)
                  || String(k.kundennr || "").toLowerCase().includes(q)
                  || (k.ort || "").toLowerCase().includes(q))
      .slice(0, 10);
  }, [begriff, kunden]);

  const waehlen = (k) => {
    onWahl(k);
    setBegriff("");
    setOffen(false);
  };

  const tasten = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAktiv((a) => Math.min(a + 1, treffer.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setAktiv((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && treffer[aktiv]) { e.preventDefault(); waehlen(treffer[aktiv]); }
    else if (e.key === "Enter" && freitext && begriff.trim()) { e.preventDefault(); waehlen({ name: begriff.trim(), customer_id: null, manuell: true }); }
    else if (e.key === "Escape") { setOffen(false); }
  };

  // Ist bereits ein Kunde gewählt, zeigt die Komponente ihn an – mit der
  // Möglichkeit, die Wahl wieder aufzuheben.
  if (wert) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
           data-testid={`${testId}-gewaehlt`}>
        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-emerald-900 truncate">{wert.name}</div>
          <div className="text-xs text-emerald-700">
            {wert.manuell ? "von Hand eingetragen, noch nicht im Kundenstamm" : null}
            {wert.kundennr && <>Nr. {wert.kundennr}</>}
            {wert.kundennr && wert.ort && " · "}
            {wert.ort}
          </div>
        </div>
        <button type="button" onClick={() => onWahl(null)}
                className="text-emerald-700 hover:text-emerald-900 shrink-0"
                title="Andere Wahl treffen" data-testid={`${testId}-loesen`}>
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={kasten} className="relative" data-testid={testId}>
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        ref={feld}
        value={begriff}
        autoFocus={autoFocus}
        onChange={(e) => { setBegriff(e.target.value); setOffen(true); }}
        onFocus={() => setOffen(true)}
        onKeyDown={tasten}
        placeholder="Name, Kundennummer oder Ort…"
        data-testid={`${testId}-feld`}
        className="h-10 md:h-9 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3
                   text-base md:text-sm text-slate-900 placeholder:text-slate-400
                   outline-none transition-colors focus:border-slate-400"
      />
      {offen && begriff.trim() && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-64 overflow-y-auto
                        rounded-md border border-slate-200 bg-white shadow-lg"
             data-testid={`${testId}-treffer`}>
          {!kunden ? (
            <div className="p-3 flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Kunden werden geladen…
            </div>
          ) : treffer.length === 0 && !freitext ? (
            <div className="p-3 text-sm text-slate-400">
              Kein Kunde gefunden. Handelt es sich um einen Interessenten,
              lässt er sich oben als Telefon-Lead anlegen.
            </div>
          ) : (
            <>
            {treffer.map((k, i) => (
              <button
                key={k.customer_id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); waehlen(k); }}
                onMouseEnter={() => setAktiv(i)}
                data-testid={`${testId}-treffer-${k.customer_id}`}
                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3
                            ${i === aktiv ? "bg-slate-100" : "bg-white"}`}
              >
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-900 truncate">{k.name}</span>
                  {k.ort && <span className="block text-xs text-slate-500">{k.plz} {k.ort}</span>}
                </span>
                {k.kundennr && (
                  <span className="text-xs text-slate-400 tabular shrink-0">Nr. {k.kundennr}</span>
                )}
              </button>
            ))}
            {freitext && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); waehlen({ name: begriff.trim(), customer_id: null, manuell: true }); }}
                      data-testid={`${testId}-freitext`}
                      className="w-full text-left px-3 py-2 flex items-center gap-2 border-t border-slate-100 bg-amber-50/60 hover:bg-amber-50">
                <span className="text-sm text-slate-900">„{begriff.trim()}“ als Kunden von Hand eintragen</span>
                <span className="text-[11px] text-slate-500 ml-auto shrink-0">nicht im Kundenstamm</span>
              </button>
            )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
